import React, { useMemo, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { InputBase, TextField, Typography, useMediaQuery } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import InboxOutlinedIcon from '@material-ui/icons/InboxOutlined';
import moment from 'moment';

import { EstadoEiffelChip, EstadoFacturaChip } from '../EstadoSmartScan/EstadoSmartScan';
import { esPendiente, estadoFactura, estadosFactura, formatFecha, formatImporte } from '../datosSmartScan';
import colores from '../../Impresion/Bandeja/coloresBandeja';

const useStyles = makeStyles((theme) => ({
  contenedor: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    minHeight: 0,
    flex: 1
  },
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: '12px',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
    }
  },
  tile: {
    backgroundColor: 'white',
    border: `1px solid ${colores.borde}`,
    borderRadius: '6px',
    boxShadow: colores.sombra,
    padding: '12px 16px',
    borderTop: '4px solid',
    cursor: 'pointer'
  },
  tileValor: {
    fontSize: '26px',
    fontWeight: 500,
    lineHeight: 1.2,
    [theme.breakpoints.down('xs')]: {
      fontSize: '20px'
    }
  },
  tileEtiqueta: {
    fontSize: '12px',
    color: colores.secundario
  },
  filtros: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    padding: '10px 14px',
    backgroundColor: 'white',
    border: `1px solid ${colores.borde}`,
    borderRadius: '6px',
    boxShadow: colores.sombra
  },
  buscador: {
    flex: 1,
    minWidth: '220px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    border: `1px solid ${colores.borde}`,
    borderRadius: '20px',
    padding: '2px 12px',
    '&:focus-within': {
      borderColor: colores.primario
    },
    [theme.breakpoints.down('xs')]: {
      flexBasis: '100%',
      minWidth: 0
    }
  },
  filtro: {
    minWidth: '150px',
    [theme.breakpoints.down('xs')]: {
      flex: 1
    }
  },
  tabla: {
    backgroundColor: 'white',
    border: `1px solid ${colores.borde}`,
    borderRadius: '6px',
    boxShadow: colores.sombra,
    overflow: 'auto',
    minHeight: 0,
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '14px'
    },
    '& th': {
      position: 'sticky',
      top: 0,
      backgroundColor: '#f6f6f6',
      textAlign: 'left',
      padding: '8px 14px',
      fontSize: '11px',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: colores.secundario,
      borderBottom: `1px solid ${colores.linea}`,
      zIndex: 1
    },
    '& td': {
      padding: '10px 14px',
      borderBottom: `1px solid ${colores.linea}`
    },
    '& tbody tr': {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: colores.primarioClaro
      }
    }
  },
  derecha: {
    textAlign: 'right !important',
    whiteSpace: 'nowrap'
  },
  sub: {
    fontSize: '12px',
    color: colores.tenue
  },
  tarjeta: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: '4px 12px',
    padding: '12px',
    borderBottom: `1px solid ${colores.linea}`,
    cursor: 'pointer'
  },
  vacio: {
    padding: '40px',
    textAlign: 'center',
    color: colores.tenue
  }
}));

const opcionesEstado = [
  { valor: 'todos', nombre: 'Todos los estados' },
  { valor: 'revisar', nombre: 'Para revisar' },
  ...Object.values(estadosFactura).map((estado) => ({ valor: estado.clave, nombre: estado.nombre }))
];

const coincide = (factura, busqueda) =>
  !busqueda || [factura.proveedor, factura.cuit, factura.numero].some((valor) => (valor || '').toLowerCase().includes(busqueda));

/**
 * Todas las facturas que leyó SmartScan: resumen por estado, filtros y la tabla (tarjetas en celular).
 * Al tocar una se abre en la bandeja que corresponde (para revisar o aprobadas).
 */
const TodasFacturas = ({ facturas, onAbrir }) => {
  const classes = useStyles();
  const celular = useMediaQuery(useTheme().breakpoints.down('xs'));
  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState('todos');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  const visibles = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return facturas.filter(
      (factura) =>
        coincide(factura, texto) &&
        (estado === 'todos' || (estado === 'revisar' ? esPendiente(factura) : estadoFactura(factura).clave === estado)) &&
        (!desde || (factura.fecha && factura.fecha >= desde)) &&
        (!hasta || (factura.fecha && factura.fecha <= hasta))
    );
  }, [facturas, busqueda, estado, desde, hasta]);

  const cantidad = (clave) => facturas.filter((factura) => estadoFactura(factura).clave === clave).length;
  const totalAprobado = facturas.filter((f) => f.estado === 'aprobada').reduce((suma, f) => suma + (f.total || 0), 0);

  const tiles = [
    { valor: facturas.filter(esPendiente).length, etiqueta: 'Para revisar', color: '#fb8c00', filtro: 'revisar' },
    { valor: cantidad('lista'), etiqueta: 'Listas para aprobar', color: colores.primario, filtro: 'lista' },
    { valor: cantidad('aprobada'), etiqueta: 'Aprobadas', color: colores.ok, filtro: 'aprobada' },
    { valor: formatImporte(totalAprobado), etiqueta: 'Importe aprobado', color: colores.naranja, filtro: 'aprobada' }
  ];

  const chipEstado = (factura) =>
    factura.estado === 'aprobada' ? (
      <EstadoEiffelChip eiffel={factura.eiffel} style={{ height: '22px' }} />
    ) : (
      <EstadoFacturaChip factura={factura} style={{ height: '22px' }} />
    );

  return (
    <div className={classes.contenedor}>
      <div className={classes.tiles}>
        {tiles.map((tile) => (
          <div key={tile.etiqueta} className={classes.tile} style={{ borderTopColor: tile.color }} onClick={() => setEstado(tile.filtro)}>
            <div className={classes.tileValor}>{tile.valor}</div>
            <div className={classes.tileEtiqueta}>{tile.etiqueta}</div>
          </div>
        ))}
      </div>

      <div className={classes.filtros}>
        <div className={classes.buscador}>
          <SearchIcon style={{ color: colores.tenue }} fontSize="small" />
          <InputBase fullWidth placeholder="Buscar por proveedor, CUIT o número…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
        </div>
        <TextField
          select
          size="small"
          label="Estado"
          className={classes.filtro}
          value={estado}
          onChange={(e) => setEstado(e.target.value)}
          SelectProps={{ native: true }}
        >
          {opcionesEstado.map((opcion) => (
            <option key={opcion.valor} value={opcion.valor}>
              {opcion.nombre}
            </option>
          ))}
        </TextField>
        <TextField size="small" type="date" label="Fecha desde" className={classes.filtro} InputLabelProps={{ shrink: true }} value={desde} onChange={(e) => setDesde(e.target.value)} />
        <TextField size="small" type="date" label="Fecha hasta" className={classes.filtro} InputLabelProps={{ shrink: true }} value={hasta} onChange={(e) => setHasta(e.target.value)} />
      </div>

      <div className={classes.tabla}>
        {visibles.length === 0 ? (
          <div className={classes.vacio}>
            <InboxOutlinedIcon style={{ fontSize: 40 }} />
            <Typography variant="body2">No hay facturas con esos filtros</Typography>
          </div>
        ) : celular ? (
          visibles.map((factura) => (
            <div key={factura.id} className={classes.tarjeta} onClick={() => onAbrir(factura)}>
              <b>{factura.proveedor}</b>
              <b className={classes.derecha}>{formatImporte(factura.total)}</b>
              <span className={classes.sub}>{`FC ${factura.letra} ${factura.numero} · ${formatFecha(factura.fecha)}`}</span>
              <span className={classes.derecha}>{chipEstado(factura)}</span>
            </div>
          ))
        ) : (
          <table>
            <thead>
              <tr>
                <th>Proveedor</th>
                <th>Número</th>
                <th>Fecha</th>
                <th>Recibida</th>
                <th className={classes.derecha}>Total</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((factura) => (
                <tr key={factura.id} onClick={() => onAbrir(factura)}>
                  <td>
                    {factura.proveedor}
                    <div className={classes.sub}>{`CUIT ${factura.cuit}`}</div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{`FC ${factura.letra} ${factura.numero}`}</td>
                  <td>{formatFecha(factura.fecha)}</td>
                  <td>
                    {moment(factura.recibida).format('DD/MM HH:mm')}
                    <div className={classes.sub}>{factura.origen}</div>
                  </td>
                  <td className={classes.derecha}>{formatImporte(factura.total)}</td>
                  <td>{chipEstado(factura)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default TodasFacturas;
